/**
 * Zoho OAuth callback relay.
 *
 * Zoho redirects here with `?code=...&state=...` after the user approves
 * consent. This page extracts the params and forwards them to the
 * zoho-oauth-callback Supabase Edge Function, which performs the actual
 * code-for-refresh-token exchange and persists the result.
 *
 * The admin SPA cannot do the exchange itself: the Zoho client_secret
 * must never ship in a browser bundle.
 */
import { useEffect, useState } from 'react';

const FUNCTION_URL = import.meta.env.VITE_ZOHO_OAUTH_CALLBACK_FUNCTION_URL;

type Status =
  | { kind: 'pending' }
  | { kind: 'success' }
  | { kind: 'error'; detail: string };

export function ZohoOAuthCallbackPage() {
  const [status, setStatus] = useState<Status>({ kind: 'pending' });

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const code = params.get('code');
    const state = params.get('state');
    const error = params.get('error');

    if (error) {
      setStatus({ kind: 'error', detail: `Zoho returned: ${error}` });
      return;
    }
    if (!code) {
      setStatus({ kind: 'error', detail: 'No authorization code in callback URL.' });
      return;
    }
    if (!FUNCTION_URL) {
      setStatus({
        kind: 'error',
        detail: 'VITE_ZOHO_OAUTH_CALLBACK_FUNCTION_URL is not set.',
      });
      return;
    }

    const target = new URL(FUNCTION_URL);
    target.searchParams.set('code', code);
    if (state) target.searchParams.set('state', state);

    fetch(target.toString(), { method: 'GET' })
      .then(async (res) => {
        if (!res.ok) {
          const detail = await res.text();
          throw new Error(`Edge function ${res.status}: ${detail}`);
        }
        setStatus({ kind: 'success' });
      })
      .catch((err: unknown) => {
        setStatus({
          kind: 'error',
          detail: err instanceof Error ? err.message : String(err),
        });
      });
  }, []);

  return (
    <div className="mx-auto max-w-xl px-6 py-16">
      <h1 className="text-2xl font-semibold text-text">Zoho integration</h1>
      {status.kind === 'pending' && (
        <p className="mt-4 text-text-muted" role="status" aria-live="polite">
          Exchanging authorization code…
        </p>
      )}
      {status.kind === 'success' && (
        <div role="status" className="mt-4 rounded-md border border-success/30 bg-success-soft p-4 text-success">
          <p className="font-medium">Connected.</p>
          <p className="mt-1 text-sm">
            Refresh token stored. The sync worker can now talk to Zoho.
          </p>
        </div>
      )}
      {status.kind === 'error' && (
        <div role="alert" className="mt-4 rounded-md border border-error/30 bg-error-soft p-4 text-error">
          <p className="font-medium">Connection failed.</p>
          <pre className="mt-2 overflow-auto whitespace-pre-wrap break-words text-xs">
            {status.detail}
          </pre>
        </div>
      )}
    </div>
  );
}
